import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Select from "react-select";
import { useToasts } from "react-toast-notifications";
import apiService from "../../Services/apiServices";
import helper from "../../Services/helper";
import calculate from "../../Services/calculate";
import ModelPurchases from "../../Models/Purchases";
import ModelPurchaseDetails from "../../Models/PurchaseDetails";
import ItemModel from "../../Models/Item";
import {
  BARCODE_HEADERS,
  buildPreparedItems,
  findBestItemMatch,
  findHeaderKey,
  ITEM_ID_HEADERS,
  ITEM_NAME_HEADERS,
  normalizeText,
  PRICE_HEADERS,
  PRODUCT_NO_HEADERS,
  QUANTITY_HEADERS,
  UNIT_HEADERS,
  VAT_HEADERS,
} from "./comparisonUtils";

const DEFAULT_GOODS_ACCOUNTS = {
  accountA: 1359,
  accountB: 1391,
  accountC: 1389,
};

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    background: "#fff",
    borderColor: state.isFocused ? "#9e9e9e" : "#9e9e9e",
    minHeight: "31px",
    height: "31px",
    boxShadow: null,
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "31px",
    padding: "0 6px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "31px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const createDetailRow = () => ({
  id: 0,
  sId: null,
  purchaseId: "",
  no: 1,
  itemId: 0,
  itemName: "",
  projectId: 0,
  invoiceId: 0,
  quantity: 1,
  price: 0,
  priceWithVat: 0,
  discount: 0,
  markup: 0,
  duty: 0,
  dutyPercentage: 0,
  transport: 0,
  overvalue: 0,
  excise: 0,
  excisePercentage: 0,
  purchasePrice: 0,
  vat: 0,
  vatForm: 0,
  vatSum: 0,
  createdAt: "",
  createdBy: "",
  changedAt: "",
  changedBy: "",
  status: 0,
  price_discount: 0,
  salePrice: 0,
  salePriceWithVat: 0,
  editMode: false,
  subTotal: 0,
  total: 0,
  transportPercentage: 0,
  dudNo: 0,
  dudDate: null,
  unitId: 0,
  unit: "",
  barcode: "",
  productNo: "",
  priceFromFile: 0,
  retailPrice: 0,
  matchType: "Not Found",
  matchScore: 0,
  matchedItemName: "",
});

const parseNumber = (value, fallback = 0) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;

  const normalizedValue = String(value || "")
    .replace(/\s+/g, "")
    .replace(",", ".");
  const parsedValue = parseFloat(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const formatPercent = (value) => `${Math.round((value || 0) * 100)}%`;

const calculateNetPriceFromGross = (grossPrice, vat) => {
  const parsedGrossPrice = parseNumber(grossPrice, 0);
  const parsedVat = parseNumber(vat, 0);
  const denominator = 1 + parsedVat / 100;

  if (!denominator) return 0;

  return parseFloat((parsedGrossPrice / denominator).toFixed(6));
};

const syncRowPricesFromGross = (row, grossPrice) => {
  const normalizedGrossPrice = parseNumber(grossPrice, 0);
  const netPrice = calculateNetPriceFromGross(normalizedGrossPrice, row.vat);

  row.priceFromFile = normalizedGrossPrice;
  row.price = netPrice;
  row.retailPrice = netPrice;

  calculate().registrationOfGoods(row, false, "price");

  return row;
};

const getMatchBadgeClass = (type) => {
  if (type === "Exact") return "badge badge-success";
  if (type === "Approx") return "badge badge-warning";
  return "badge badge-danger";
};

const buildRowKey = (row) => {
  const parts = [];

  if (row.productNo) parts.push(`product:${normalizeText(row.productNo)}`);
  if (row.barcode) parts.push(`barcode:${normalizeText(row.barcode)}`);
  parts.push(`name:${normalizeText(row.itemName)}`);

  return parts.join("|");
};

function RegistrationGoodsImport() {
  const { addToast } = useToasts();
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingFile, setProcessingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    date: helper().formatDate(new Date(new Date().getFullYear(), 0, 1), "yyyy-MM-dd"),
    branchId: 0,
    warehouseId: 0,
    comment: "",
  });

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      apiService().get("items"),
      apiService().get("units"),
      apiService().get("branches"),
    ])
      .then(([itemsResponse, unitsResponse, branchesResponse]) => {
        if (!isMounted) return;

        if (itemsResponse.success) {
          setItems(
            (itemsResponse.data || []).filter((item) => item.itemGoods === true)
          );
        } else {
          addToast(
            itemsResponse.errorMessage ||
              "Ndodhi nje gabim gjate marrjes se artikujve.",
            { appearance: "error", autoDismiss: true }
          );
        }

        if (unitsResponse.success) {
          setUnits(
            (unitsResponse.data || []).map((unit) => ({
              value: unit.typeId,
              label: unit.name,
            }))
          );
        } else {
          addToast(
            unitsResponse.errorMessage ||
              "Ndodhi nje gabim gjate marrjes se njesive.",
            { appearance: "error", autoDismiss: true }
          );
        }

        if (branchesResponse.success) {
          setBranches(
            (branchesResponse.data || []).map((branch) => ({
              value: branch.id,
              label: branch.name,
            }))
          );
        } else {
          addToast(
            branchesResponse.errorMessage ||
              "Ndodhi nje gabim gjate marrjes se degeve.",
            { appearance: "error", autoDismiss: true }
          );
        }
      })
      .catch(() => {
        if (!isMounted) return;

        addToast("Nuk u arrit te merren te dhenat fillestare nga API.", {
          appearance: "error",
          autoDismiss: true,
        });
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [addToast]);

  const unitLookup = useMemo(() => {
    const lookup = new Map();

    units.forEach((unit) => {
      lookup.set(String(unit.value), unit);
      lookup.set(normalizeText(unit.label), unit);
    });

    return lookup;
  }, [units]);

  const preparedLookup = useMemo(() => buildPreparedItems(items), [items]);

  const loadWarehousesByBranch = (branchId) => {
    if (!branchId) {
      setWarehouses([]);
      return;
    }

    apiService()
      .query("WarehousesByBranch", `branchId=${branchId}`)
      .then((response) => {
        if (response.success) {
          const warehouseOptions = (response.data || []).map((warehouse) => ({
            value: warehouse.id,
            label: warehouse.name,
          }));

          setWarehouses(warehouseOptions);
        } else {
          setWarehouses([]);
          addToast(
            response.errorMessage ||
              "Ndodhi nje gabim gjate marrjes se magazinave.",
            { appearance: "error", autoDismiss: true }
          );
        }
      })
      .catch(() => {
        setWarehouses([]);
        addToast("Nuk u arrit te merren magazinat per degen e zgjedhur.", {
          appearance: "error",
          autoDismiss: true,
        });
      });
  };

  const resolveUnitOption = (unitValue) => {
    if (unitValue === null || typeof unitValue === "undefined" || unitValue === "") {
      return null;
    }

    return (
      unitLookup.get(String(unitValue).trim()) ||
      unitLookup.get(normalizeText(unitValue)) ||
      null
    );
  };

  const buildPreviewRows = (importRows, keys) => {
    return importRows
      .map((row, index) => {
        const itemName = String(row?.[keys.itemNameKey] || "").trim();
        const barcode = keys.barcodeKey
          ? String(row?.[keys.barcodeKey] || "").trim()
          : "";
        const productNo = keys.productNoKey
          ? String(row?.[keys.productNoKey] || "").trim()
          : "";

        if (!itemName && !barcode && !productNo) {
          return null;
        }

        const quantity = keys.quantityKey
          ? parseNumber(row?.[keys.quantityKey], 1)
          : 1;
        const priceFromFile = keys.priceKey
          ? parseNumber(row?.[keys.priceKey], 0)
          : 0;
        const unitOption = keys.unitKey ? resolveUnitOption(row?.[keys.unitKey]) : null;
        const explicitItemId = keys.itemIdKey
          ? parseNumber(row?.[keys.itemIdKey], 0)
          : 0;

        let matchedItem = null;
        let matchType = "Not Found";
        let matchScore = 0;

        if (explicitItemId) {
          matchedItem = items.find((item) => item.id === explicitItemId) || null;
          matchType = matchedItem ? "Exact" : "Not Found";
          matchScore = matchedItem ? 1 : 0;
        }

        if (!matchedItem) {
          const matchResult = findBestItemMatch(
            { itemName, barcode, productNo },
            preparedLookup
          );
          matchedItem = matchResult.matchedItem;
          matchType = matchResult.matchType;
          matchScore = matchResult.matchScore;
        }

        const rawVatValue = keys.vatKey ? row?.[keys.vatKey] : "";
        const vat =
          rawVatValue === "" || typeof rawVatValue === "undefined" || rawVatValue === null
            ? matchedItem?.vat || 0
            : parseNumber(rawVatValue, 0);

        const previewRow = {
          ...createDetailRow(),
          no: index + 1,
          itemId: matchedItem?.id || 0,
          itemName,
          quantity,
          price: 0,
          vat,
          unitId: unitOption?.value || matchedItem?.unitId || 0,
          unit:
            unitOption?.label ||
            units.find((unit) => unit.value === matchedItem?.unitId)?.label ||
            "",
          barcode,
          productNo,
          matchedItemName: matchedItem?.itemName || "",
          matchType,
          matchScore,
        };

        syncRowPricesFromGross(previewRow, priceFromFile);

        return previewRow;
      })
      .filter(Boolean);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (loading) {
      addToast("Prit derisa te merren te dhenat baze nga API.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    setProcessingFile(true);
    setFileName(file.name);

    const reader = new FileReader();

    reader.onload = (loadEvent) => {
      try {
        const workbook = XLSX.read(loadEvent.target.result, {
          type: "binary",
          cellDates: true,
        });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const importRows = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          defval: "",
        });

        if (!importRows.length) {
          setRows([]);
          addToast("Excel file nuk ka rreshta per import.", {
            appearance: "warning",
            autoDismiss: true,
          });
          return;
        }

        const firstRow = importRows[0];
        const keys = {
          itemIdKey: findHeaderKey(firstRow, ITEM_ID_HEADERS),
          itemNameKey: findHeaderKey(firstRow, ITEM_NAME_HEADERS),
          quantityKey: findHeaderKey(firstRow, QUANTITY_HEADERS),
          priceKey: findHeaderKey(firstRow, PRICE_HEADERS),
          vatKey: findHeaderKey(firstRow, VAT_HEADERS),
          unitKey: findHeaderKey(firstRow, UNIT_HEADERS),
          barcodeKey: findHeaderKey(firstRow, BARCODE_HEADERS),
          productNoKey: findHeaderKey(firstRow, PRODUCT_NO_HEADERS),
        };

        if (!keys.itemNameKey) {
          setRows([]);
          addToast("Excel file duhet ta kete kolonen itemName.", {
            appearance: "error",
            autoDismiss: true,
          });
          return;
        }

        const previewRows = buildPreviewRows(importRows, keys);
        setRows(previewRows);
        setErrors({});

        addToast(
          `U pergatiten ${previewRows.length} rreshta nga ${file.name}.`,
          { appearance: "success", autoDismiss: true }
        );
      } catch (error) {
        setRows([]);
        addToast("Leximi i Excel file deshtoi.", {
          appearance: "error",
          autoDismiss: true,
        });
      } finally {
        setProcessingFile(false);
      }
    };

    reader.onerror = () => {
      setProcessingFile(false);
      setRows([]);
      addToast("Ndodhi nje gabim gjate hapjes se file-it.", {
        appearance: "error",
        autoDismiss: true,
      });
    };

    reader.readAsBinaryString(file);
  };

  const handleRowChange = (index, field, value) => {
    setRows((currentRows) => {
      const nextRows = [...currentRows];
      const nextRow = { ...nextRows[index] };

      if (field === "unitId") {
        const selectedUnit = units.find((unit) => unit.value === parseNumber(value, 0));
        nextRow.unitId = selectedUnit?.value || 0;
        nextRow.unit = selectedUnit?.label || "";
      } else if (field === "priceFromFile") {
        syncRowPricesFromGross(nextRow, value);
      } else if (field === "vat") {
        nextRow.vat = value === "" ? 0 : parseNumber(value, 0);
        syncRowPricesFromGross(nextRow, nextRow.priceFromFile);
      } else if (field === "quantity" || field === "price") {
        nextRow[field] = value === "" ? 0 : parseNumber(value, 0);
        calculate().registrationOfGoods(nextRow, false, field);
      } else {
        nextRow[field] = value;
        calculate().registrationOfGoods(nextRow, false, field);
      }
      nextRows[index] = nextRow;
      return nextRows;
    });

    setErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[`row_item_${index}`];
      delete nextErrors[`row_quantity_${index}`];
      delete nextErrors[`row_price_${index}`];
      return nextErrors;
    });
  };

  const deleteRow = (index) => {
    setRows((currentRows) =>
      currentRows
        .filter((_, rowIndex) => rowIndex !== index)
        .map((row, rowIndex) => ({ ...row, no: rowIndex + 1 }))
    );
  };

  const buildItemPayloadFromRow = (row) => {
    const newItem = new ItemModel();

    newItem.itemGoods = true;
    newItem.itemName = String(row.itemName || "").trim();
    newItem.productNo = String(row.productNo || "").trim();
    newItem.barcode = String(row.barcode || "").trim();
    newItem.unitId = row.unitId || 0;
    newItem.vat = parseNumber(row.vat, 0);
    newItem.categoryId = 0;
    newItem.retailPrice = parseNumber(row.retailPrice || row.price, 0);
    newItem.price = parseNumber(row.priceFromFile, 0);
    newItem.accountA = DEFAULT_GOODS_ACCOUNTS.accountA;
    newItem.accountB = DEFAULT_GOODS_ACCOUNTS.accountB;
    newItem.accountC = DEFAULT_GOODS_ACCOUNTS.accountC;

    return newItem;
  };

  const validateBeforeSave = () => {
    const nextErrors = {};

    if (!form.date) nextErrors.date = "Zgjidhe";
    if (!form.branchId) nextErrors.branchId = "Zgjidhe";
    if (!form.warehouseId) nextErrors.warehouseId = "Zgjidhe";
    if (!rows.length) nextErrors.rows = "Ngarko nje Excel file.";

    rows.forEach((row, index) => {
      if (!String(row.itemName || "").trim()) {
        nextErrors[`row_item_${index}`] = "Shkruaje artikullin";
      }
      if (!parseNumber(row.quantity, 0)) {
        nextErrors[`row_quantity_${index}`] = "Shkruaje sasine";
      }
      if (!parseNumber(row.price, 0)) {
        nextErrors[`row_price_${index}`] = "Shkruaje cmimin";
      }
    });

    setErrors(nextErrors);
    return !Object.keys(nextErrors).length;
  };

  const saveChunks = async (chunks) => {
    for (const chunk of chunks) {
      const response = await apiService().saveChanges(chunk);

      if (!response.success) {
        throw new Error(response.errorMessage || "Ka ndodhur nje gabim.");
      }
    }
  };

  const splitArrayIntoChunks = (array, chunkSize) => {
    const chunks = [];

    for (let index = 0; index < array.length; index += chunkSize) {
      chunks.push(array.slice(index, index + chunkSize));
    }

    return chunks;
  };

  const saveRegistration = async () => {
    if (!validateBeforeSave()) {
      addToast("Ploteso fushat e detyrueshme para ruajtjes.", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    setSaving(true);

    try {
      const createdItemsByKey = new Map();
      const nextRows = [...rows];

      for (let index = 0; index < nextRows.length; index++) {
        const currentRow = { ...nextRows[index] };

        if (!currentRow.itemId) {
          const rowKey = buildRowKey(currentRow);

          if (createdItemsByKey.has(rowKey)) {
            const existingCreatedItem = createdItemsByKey.get(rowKey);
            currentRow.itemId = existingCreatedItem.id;
            currentRow.matchType = "Created";
            currentRow.matchedItemName = existingCreatedItem.itemName;
          } else {
            const createItemResponse = await apiService().post(
              "items",
              buildItemPayloadFromRow(currentRow)
            );

            if (!createItemResponse.success) {
              throw new Error(
                createItemResponse.errorMessage ||
                  `Nuk u ruajt artikulli ${currentRow.itemName}.`
              );
            }

            createdItemsByKey.set(rowKey, createItemResponse.data);
            currentRow.itemId = createItemResponse.data.id;
            currentRow.matchType = "Created";
            currentRow.matchScore = 1;
            currentRow.matchedItemName = createItemResponse.data.itemName;
          }
        }

        nextRows[index] = currentRow;
      }

      const purchase = new ModelPurchases();
      purchase.date = form.date;
      purchase.branchId = form.branchId;
      purchase.warehouseId = form.warehouseId;
      purchase.comment = form.comment;
      purchase.partnerId = null;
      purchase.dateRegistered = new Date();
      purchase.subTotal = nextRows.reduce(
        (sum, row) => sum + parseNumber(row.subTotal, 0),
        0
      );
      purchase.vatSum = nextRows.reduce(
        (sum, row) => sum + parseNumber(row.vatSum, 0),
        0
      );
      purchase.totalSum = nextRows.reduce(
        (sum, row) => sum + parseNumber(row.total, 0),
        0
      );
      purchase.amount = purchase.totalSum;

      const purchaseResponse = await apiService().post("itemsRegistration", purchase);

      if (!purchaseResponse.success) {
        throw new Error(
          purchaseResponse.errorMessage ||
            "Nuk u ruajt header i regjistrimit te mallit."
        );
      }

      const detailEntries = nextRows
        .map((row) => {
          const detailEntity = new ModelPurchaseDetails({
            ...row,
            purchaseId: purchaseResponse.data.id,
            purchasePrice: row.price,
            vatForm: row.vatForm || 0,
          });

          return {
            id: 0,
            entity: detailEntity,
            status: 1,
            type: "purchaseDetails",
          };
        })
        .filter((entry) => entry.entity.itemId && entry.entity.price);

      await saveChunks(splitArrayIntoChunks(detailEntries, 10));

      setRows(nextRows);

      addToast("Regjistrimi i mallit u ruajt me sukses.", {
        appearance: "success",
        autoDismiss: true,
      });

      window.location.href = `#/finances/registration-goods/${purchaseResponse.data.id}`;
    } catch (error) {
      addToast(error.message || "Ka ndodhur nje gabim gjate ruajtjes.", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const exactCount = rows.filter((row) => row.matchType === "Exact").length;
  const approxCount = rows.filter((row) => row.matchType === "Approx").length;
  const newCount = rows.filter((row) => !row.itemId).length;

  return (
    <div className="container-fluid px-2 mt-3">
      <div className="card shadow">
        <div className="card-header py-2 d-flex flex-wrap align-items-center justify-content-between">
          <h6 className="m-0 font-weight-bold text-primary">
            Import Regjistrimi i Mallit
          </h6>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={saveRegistration}
            disabled={loading || processingFile || saving || !rows.length}
          >
            {saving ? "Duke ruajtur..." : "Ruaje Regjistrimin"}
          </button>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-lg-4 mb-3">
              <label className="font-weight-bold mb-1">Excel file</label>
              <input
                type="file"
                className={`form-control ${errors.rows ? "is-invalid" : ""}`}
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={loading || processingFile || saving}
              />
              <small className="form-text text-muted">
                Kolonat minimale: <strong>itemName</strong>,{" "}
                <strong>quantity</strong>, <strong>price</strong>.
                <br />
                Kolona <strong>price</strong> trajtohet si cmim me TVSH.
              </small>
              {fileName ? (
                <small className="d-block text-muted mt-2">
                  File i ngarkuar: {fileName}
                </small>
              ) : null}
              {errors.rows ? <small className="text-danger">{errors.rows}</small> : null}
            </div>

            <div className="col-lg-2 mb-3">
              <label className="font-weight-bold mb-1">Data</label>
              <input
                type="date"
                className={`form-control ${errors.date ? "is-invalid" : ""}`}
                value={form.date}
                onChange={(event) =>
                  setForm((currentForm) => ({
                    ...currentForm,
                    date: event.target.value,
                  }))
                }
              />
            </div>

            <div className="col-lg-3 mb-3">
              <label className="font-weight-bold mb-1">Dega</label>
              <Select
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={branches}
                value={branches.find((branch) => branch.value === form.branchId) || null}
                onChange={(selectedBranch) => {
                  const nextBranchId = selectedBranch?.value || 0;

                  setForm((currentForm) => ({
                    ...currentForm,
                    branchId: nextBranchId,
                    warehouseId: 0,
                  }));
                  setErrors((currentErrors) => ({
                    ...currentErrors,
                    branchId: "",
                    warehouseId: "",
                  }));
                  loadWarehousesByBranch(nextBranchId);
                }}
                placeholder="Zgjidhe degen"
                className={errors.branchId ? "is-invalid-s" : ""}
              />
            </div>

            <div className="col-lg-3 mb-3">
              <label className="font-weight-bold mb-1">Magazina</label>
              <Select
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={warehouses}
                value={
                  warehouses.find((warehouse) => warehouse.value === form.warehouseId) ||
                  null
                }
                onChange={(selectedWarehouse) => {
                  setForm((currentForm) => ({
                    ...currentForm,
                    warehouseId: selectedWarehouse?.value || 0,
                  }));
                  setErrors((currentErrors) => ({
                    ...currentErrors,
                    warehouseId: "",
                  }));
                }}
                placeholder="Zgjidhe magazinen"
                className={errors.warehouseId ? "is-invalid-s" : ""}
                isDisabled={!form.branchId}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-lg-6 mb-3 mb-lg-0">
              <label className="font-weight-bold mb-1">Koment</label>
              <textarea
                className="form-control"
                rows="2"
                value={form.comment}
                onChange={(event) =>
                  setForm((currentForm) => ({
                    ...currentForm,
                    comment: event.target.value,
                  }))
                }
              />
              <small className="form-text text-muted">
                Artikujt e rinj krijohen si <strong>artikuj mall</strong> me konto
                default <strong>1359 / 1391 / 1389</strong>. `VAT` lexohet nga
                file per secilin rresht, ndersa `RetailPrice` llogaritet pa TVSH.
              </small>
            </div>

            <div className="col-lg-6">
              <div className="row">
                <div className="col-md-4 mb-2">
                  <div className="border rounded p-3 h-100">
                    <div className="text-xs text-uppercase text-muted mb-1">Exact</div>
                    <div className="h5 mb-0 text-success">{exactCount}</div>
                  </div>
                </div>
                <div className="col-md-4 mb-2">
                  <div className="border rounded p-3 h-100">
                    <div className="text-xs text-uppercase text-muted mb-1">Approx</div>
                    <div className="h5 mb-0 text-warning">{approxCount}</div>
                  </div>
                </div>
                <div className="col-md-4 mb-2">
                  <div className="border rounded p-3 h-100">
                    <div className="text-xs text-uppercase text-muted mb-1">Te Reja</div>
                    <div className="h5 mb-0 text-danger">{newCount}</div>
                  </div>
                </div>
              </div>
              <div className="small text-muted mt-2">
                Exact dhe Approx marrin `itemId` ekzistues. Rreshtat pa ndeshje
                krijohen automatikisht si artikuj te rinj para ruajtjes se
                regjistrimit.
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-hover mb-0">
              <thead className="thead-light">
                <tr>
                  <th style={{ width: "55px" }}>Nr</th>
                  <th>{helper().translate("ITEMNAME")}</th>
                  <th style={{ width: "200px" }}>Artikulli / Match</th>
                  <th style={{ width: "120px" }}>Statusi</th>
                  <th style={{ width: "90px" }}>Sasia</th>
                  <th style={{ width: "120px" }}>Cmimi me TVSH</th>
                  <th style={{ width: "90px" }}>TVSH</th>
                  <th style={{ width: "120px" }}>RetailPrice</th>
                  <th style={{ width: "150px" }}>Njesia</th>
                  <th style={{ width: "120px" }}>Totali</th>
                  <th style={{ width: "70px" }}></th>
                </tr>
              </thead>
              <tbody>
                {!rows.length ? (
                  <tr>
                    <td colSpan="11" className="text-center p-4">
                      {processingFile
                        ? "Duke e lexuar Excel file..."
                        : loading
                        ? "Duke i marre te dhenat..."
                        : helper().translate("NO_DATA")}
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={`${row.no}-${row.itemName}-${index}`}>
                      <td>{row.no}</td>
                      <td>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${
                            errors[`row_item_${index}`] ? "is-invalid" : ""
                          }`}
                          value={row.itemName}
                          onChange={(event) =>
                            handleRowChange(index, "itemName", event.target.value)
                          }
                        />
                        <small className="text-muted d-block mt-1">
                          {row.productNo ? `Shifra: ${row.productNo}` : ""}
                          {row.productNo && row.barcode ? " | " : ""}
                          {row.barcode ? `Barcode: ${row.barcode}` : ""}
                        </small>
                        {errors[`row_item_${index}`] ? (
                          <small className="text-danger">
                            {errors[`row_item_${index}`]}
                          </small>
                        ) : null}
                      </td>
                      <td>
                        {row.itemId ? (
                          <a href={`#/item/${row.itemId}`}>
                            {row.matchedItemName || row.itemName}
                          </a>
                        ) : (
                          <span className="text-muted">Do te krijohet artikull i ri</span>
                        )}
                        <small className="text-muted d-block mt-1">
                          Score: {formatPercent(row.matchScore)}
                        </small>
                      </td>
                      <td>
                        <span className={getMatchBadgeClass(row.matchType)}>
                          {row.matchType}
                        </span>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          className={`form-control form-control-sm text-center ${
                            errors[`row_quantity_${index}`] ? "is-invalid" : ""
                          }`}
                          value={row.quantity}
                          onChange={(event) =>
                            handleRowChange(index, "quantity", event.target.value)
                          }
                        />
                        {errors[`row_quantity_${index}`] ? (
                          <small className="text-danger d-block mt-1">
                            {errors[`row_quantity_${index}`]}
                          </small>
                        ) : null}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          className={`form-control form-control-sm text-center ${
                            errors[`row_price_${index}`] ? "is-invalid" : ""
                          }`}
                          value={row.priceFromFile}
                          onChange={(event) =>
                            handleRowChange(index, "priceFromFile", event.target.value)
                          }
                        />
                        {errors[`row_price_${index}`] ? (
                          <small className="text-danger d-block mt-1">
                            {errors[`row_price_${index}`]}
                          </small>
                        ) : null}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          className="form-control form-control-sm text-center"
                          value={row.vat}
                          onChange={(event) =>
                            handleRowChange(index, "vat", event.target.value)
                          }
                        />
                      </td>
                      <td className="text-right align-middle">
                        {helper().formatNumber(parseNumber(row.retailPrice, 0))}
                      </td>
                      <td>
                        <select
                          className="form-control form-control-sm"
                          value={row.unitId || ""}
                          onChange={(event) =>
                            handleRowChange(index, "unitId", event.target.value)
                          }
                        >
                          <option value="">Pa njesi</option>
                          {units.map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="text-right align-middle">
                        {helper().formatNumber(parseNumber(row.total, 0))}
                      </td>
                      <td className="text-center align-middle">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteRow(index)}
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegistrationGoodsImport;
